import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  GENERAL_PARTNERSHIP_DISCLOSURE_ES,
  PARTNER_CATEGORY_LABELS_ES,
  PARTNER_SUCCESS_MESSAGE,
  PARTNERSHIP_ANALYTICS_EVENTS,
  getCategoryLabel,
} from "../../../constants/automapPartnershipConstants";
import { submitPartnerLead, trackPartnershipEvent } from "../../../services/automapPartnershipApi";
import { getConversionAttributionContext } from "../../../utils/campaignAttribution";
import { getPageAttributionContext } from "../../../utils/pageAttributionContext";
import PartnerDisclosure from "./PartnerDisclosure";
import "../../../styles/partnerships.css";

export default function PartnerLeadForm({
  category,
  listingId = null,
  dealerId = null,
  defaultVehicleContext = null,
  sourcePage = null,
  sourceComponent = null,
  sourceAction = null,
  defaultCity = "",
  defaultName = "",
  defaultWhatsapp = "",
  defaultEmail = "",
  quizSessionId = null,
  onSuccess,
  onCancel,
  inline = true,
}) {
  const location = useLocation();
  const [name, setName] = useState(defaultName);
  const [whatsapp, setWhatsapp] = useState(defaultWhatsapp);
  const [email, setEmail] = useState(defaultEmail);
  const [city, setCity] = useState(defaultCity || defaultVehicleContext?.city || "");
  const [message, setMessage] = useState("");
  const [consent, setConsent] = useState(false);
  const [disclosure, setDisclosure] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const pageContext = useMemo(
    () => getPageAttributionContext(location.pathname),
    [location.pathname]
  );

  useEffect(() => {
    trackPartnershipEvent(PARTNERSHIP_ANALYTICS_EVENTS.FORM_OPENED, {
      category,
      sourcePage: sourcePage || pageContext.pagePath,
      sourceComponent,
    });
  }, [category, sourcePage, sourceComponent, pageContext.pagePath]);

  const vehicleFields = useMemo(() => {
    if (!defaultVehicleContext) return {};
    return {
      vehicleMake: defaultVehicleContext.make || defaultVehicleContext.vehicleMake || null,
      vehicleModel: defaultVehicleContext.model || defaultVehicleContext.vehicleModel || null,
      vehicleYear: defaultVehicleContext.year || defaultVehicleContext.vehicleYear || null,
      vehiclePriceCop: defaultVehicleContext.priceCop ?? defaultVehicleContext.vehiclePriceCop ?? null,
    };
  }, [defaultVehicleContext]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    if (!consent || !disclosure) {
      setError("Debes aceptar el consentimiento y la divulgación para continuar.");
      return;
    }
    setSubmitting(true);
    try {
      const attributionContext = {
        ...getConversionAttributionContext({
          conversionType: "partner_lead",
          pageType: pageContext.pageType,
          listingId: listingId || pageContext.listingId,
          dealerId: dealerId || pageContext.dealerId,
        }),
        quizSessionId,
        recommendationContext: defaultVehicleContext?.recommendationContext || null,
      };
      trackPartnershipEvent(PARTNERSHIP_ANALYTICS_EVENTS.FORM_SUBMITTED, {
        category,
        sourcePage: sourcePage || pageContext.pagePath,
        sourceComponent,
        sourceAction,
      });
      trackPartnershipEvent(PARTNERSHIP_ANALYTICS_EVENTS.DISCLOSURE_ACCEPTED, { category });
      const result = await submitPartnerLead({
        category,
        buyerName: name.trim(),
        buyerWhatsapp: whatsapp.trim() || null,
        buyerEmail: email.trim() || null,
        buyerCity: city.trim() || null,
        listingId,
        dealerId,
        ...vehicleFields,
        buyerMessage: message.trim() || null,
        requestedService: getCategoryLabel(category),
        sourcePage: sourcePage || pageContext.pagePath,
        sourceComponent,
        sourceAction,
        attributionContext,
        consentAccepted: true,
        disclosureAccepted: true,
      });
      setSuccess(true);
      onSuccess?.(result);
    } catch (err) {
      setError(err.message || "No se pudo enviar la solicitud.");
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="partnerships-form__success">
        <p>{PARTNER_SUCCESS_MESSAGE}</p>
        {onCancel ? (
          <button type="button" className="partnerships-btn partnerships-btn--soft" onClick={onCancel}>
            Cerrar
          </button>
        ) : null}
      </div>
    );
  }

  const form = (
    <form className="partnerships-form" onSubmit={handleSubmit}>
      <h3>Solicitar información — {PARTNER_CATEGORY_LABELS_ES[category] || category}</h3>
      <label>
        Nombre *
        <input value={name} onChange={(e) => setName(e.target.value)} required />
      </label>
      <label>
        WhatsApp *
        <input value={whatsapp} onChange={(e) => setWhatsapp(e.target.value)} placeholder="+57..." required />
      </label>
      <label>
        Email (opcional)
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
      </label>
      <label>
        Ciudad
        <input value={city} onChange={(e) => setCity(e.target.value)} />
      </label>
      {defaultVehicleContext ? (
        <p className="muted" style={{ fontSize: "0.88rem", margin: 0 }}>
          Vehículo: {[defaultVehicleContext.year, defaultVehicleContext.make, defaultVehicleContext.model]
            .filter(Boolean)
            .join(" ") || "Contexto del vehículo adjunto"}
        </p>
      ) : null}
      <label>
        Mensaje (opcional)
        <textarea rows={3} value={message} onChange={(e) => setMessage(e.target.value)} />
      </label>
      <PartnerDisclosure category={category} compact />
      <label className="partnerships-form__checkbox">
        <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
        <span>Acepto que AutoMapa procese mis datos para conectar mi solicitud con un aliado.</span>
      </label>
      <label className="partnerships-form__checkbox">
        <input type="checkbox" checked={disclosure} onChange={(e) => setDisclosure(e.target.checked)} />
        <span>{GENERAL_PARTNERSHIP_DISCLOSURE_ES}</span>
      </label>
      {error ? <p style={{ color: "#b91c1c", margin: 0 }}>{error}</p> : null}
      <div className="partnerships-card__actions">
        <button type="submit" className="partnerships-btn partnerships-btn--primary" disabled={submitting}>
          {submitting ? "Enviando…" : "Solicitar información"}
        </button>
        {onCancel ? (
          <button type="button" className="partnerships-btn partnerships-btn--soft" onClick={onCancel}>
            Cancelar
          </button>
        ) : null}
      </div>
    </form>
  );

  return inline ? form : form;
}

export function PartnerLeadFormModal({ open, onClose, ...props }) {
  if (!open) return null;
  return (
    <div className="partnerships-modal-backdrop" role="dialog" aria-modal="true">
      <div className="partnerships-modal">
        <div className="partnerships-modal__header">
          <h3>Solicitud de servicio</h3>
          <button type="button" className="partnerships-modal__close" onClick={onClose} aria-label="Cerrar">
            ×
          </button>
        </div>
        <PartnerLeadForm {...props} onCancel={onClose} inline />
      </div>
    </div>
  );
}
